import { css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { OmegaComponent, events } from '@omegagrid/core';
import { msg } from '@omegagrid/localize';
import { ContextData, ReportContext, TextViewOptions } from '@reporting/rp-types/analytics';
import { BaseVisualizationEditor } from './baseEditor';
import { style } from './baseEditor.style';
import rp from '@reporting/rp-app-host';
import type { TextVisualization } from '../text';

const FONT_SIZES = [0, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48, 64];

/**
 * Editor of the text visualization. The text item cannot be opened
 * standalone, the editor is displayed as a subeditor inside the
 * dashboard editor ("Visualization" panel).
 */
@customElement('rp-visual-text-editor')
export class TextVisualizationEditor extends BaseVisualizationEditor<TextVisualization> {

	static styles = [OmegaComponent.styles, style, css`
		og-button.link {
			border: 1px solid var(--og-colortype-accent-border-color);
			width: 100%;
		}

		.row textarea {
			width: 100%;
			min-height: 80px;
			box-sizing: border-box;
			resize: vertical;
			border: 1px solid var(--og-colortype-accent-border-color);
			border-radius: var(--og-base-radius);
			background-color: transparent;
			color: var(--og-text-color-3);
			font-family: inherit;
			font-size: inherit;
			outline: none;
		}
	`];

	get contextData() { return this.reportContext?.context as ContextData }

	get viewOptions(): TextViewOptions {
		const context = this.contextData;
		if (!context) return {};
		context.view_options ??= {};
		return context.view_options as TextViewOptions;
	}

	willUpdate() {
		this.grid = false;
	}

	private updateOption(key: string, value: unknown) {
		const parts = key.split('.');
		let record = this.viewOptions as Record<string, any>;
		for (let i = 0; i < parts.length - 1; i++) {
			record[parts[i]] ??= {};
			record = record[parts[i]];
		}
		record[parts[parts.length - 1]] = value;

		this.visualization?.refresh(this.contextData);
		this.visualization?.dispatchEditEvent();

		// html mode toggles the css field visibility
		if (key == 'html') this.requestUpdate();
	}

	_onInputChange = (e: events.ChangeEvent) => {
		const { key, type } = (e.target as HTMLElement).dataset;

		let value: unknown = e.value;
		switch (type) {
			case 'number':
				value = Number(value);
				break;
			case 'boolean':
				value = value === '1';
				break;
			case 'color':
				value = (value as string || '').replace(/^#+/g, '');
				break;
		}

		this.updateOption(key, value);
	}

	_onTextChange = (e: Event) => {
		this.updateOption('content', (e.target as HTMLTextAreaElement).value);
	}

	/** link the text to a saved view (the text then opens it like a link) */
	async selectLink() {
		const contexts = await rp.ms('analytics').get<ReportContext[]>('namedContext', {
			fields: ['id_context', 'name'],
			order: [['name', 'asc']]
		});

		const cmd = await rp.host.openMenu({
			text: msg('Link to view'),
			mode: 'search',
			content: [
				{id: '0', name: `<${msg('none')}>`},
				...(contexts?.map(c => ({id: c.id_context.toString(), name: `#${c.id_context} ${c.name}`})) ?? [])
			]
		});
		if (cmd?.id == null) return;

		const idContext = parseInt(cmd.id);
		this.updateOption('id_context', idContext > 0 ? idContext : undefined);
		this.requestUpdate();
	}

	render = () => {
		const options = this.viewOptions;
		const style = options.style ?? {};
		const alignment = style.alignment ?? {};
		return html`
			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="html"
					data-type="boolean"
					@change="${this._onInputChange}"
					.caption="${msg('HTML mode')}"
					.items="${[{k: '0', v: msg('No')}, {k: '1', v: msg('Yes'), icon: 'code'}]}"
					.value="${options.html ? '1' : '0'}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<textarea
					.value="${options.content || ''}"
					placeholder="${msg('Text')}"
					@input="${this._onTextChange}"></textarea>
			</div>

			${options.html ? html`
				<div class="row">
					<textarea
						.value="${options.css_style || ''}"
						placeholder="${msg('CSS style')}"
						@input="${(e: Event) => this.updateOption('css_style', (e.target as HTMLTextAreaElement).value)}"></textarea>
				</div>
			` : ''}

			<div class="row">
				<og-button
					class="field link"
					color="transparent"
					.icon="${{icon: 'link'}}"
					@click="${() => this.selectLink()}">
					<div slot="content">${options.id_context
						? `${msg('Link')}: #${options.id_context}`
						: msg('Link to view')
					}</div>
				</og-button>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.sz"
					data-type="number"
					@change="${this._onInputChange}"
					.caption="${msg('Font size')}"
					.items="${FONT_SIZES.map(size => ({k: size.toString(), v: size ? size.toString() : 'auto'}))}"
					.value="${(style.sz || 0).toString()}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<og-dropdown-colorpicker
					class="field"
					color="transparent"
					data-key="style.color"
					data-type="color"
					@change="${this._onInputChange}"
					.caption="${msg('Color')}"
					.value="${style.color}">
				</og-dropdown-colorpicker>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.alignment.horizontal"
					data-type="string"
					@change="${this._onInputChange}"
					.caption="${msg('Alignment')}"
					.items="${[
						{k: 'left', v: msg('Left'), icon: 'align-left'},
						{k: 'center', v: msg('Center'), icon: 'align-center'},
						{k: 'right', v: msg('Right'), icon: 'align-right'},
					]}"
					.value="${alignment.horizontal || 'center'}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.alignment.vertical"
					data-type="string"
					@change="${this._onInputChange}"
					.caption="${msg('Vertical alignment')}"
					.items="${[
						{k: 'top', v: msg('Top'), icon: 'objects-align-top'},
						{k: 'center', v: msg('Middle'), icon: 'objects-align-center-vertical'},
						{k: 'bottom', v: msg('Bottom'), icon: 'objects-align-bottom'},
					]}"
					.value="${alignment.vertical || 'center'}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.bold"
					data-type="boolean"
					@change="${this._onInputChange}"
					.caption="${msg('Bold')}"
					.items="${[{k: '0', v: msg('No')}, {k: '1', v: msg('Yes'), icon: 'bold'}]}"
					.value="${style.bold ? '1' : '0'}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.italic"
					data-type="boolean"
					@change="${this._onInputChange}"
					.caption="${msg('Italic')}"
					.items="${[{k: '0', v: msg('No')}, {k: '1', v: msg('Yes'), icon: 'italic'}]}"
					.value="${style.italic ? '1' : '0'}">
				</og-dropdown-list>
			</div>

			<div class="row">
				<og-dropdown-list
					class="field"
					color="transparent"
					data-key="style.underline"
					data-type="boolean"
					@change="${this._onInputChange}"
					.caption="${msg('Underline')}"
					.items="${[{k: '0', v: msg('No')}, {k: '1', v: msg('Yes'), icon: 'underline'}]}"
					.value="${style.underline ? '1' : '0'}">
				</og-dropdown-list>
			</div>
		`;
	}

}
